<script setup lang="ts">
import { useRoute } from 'vue-router';

const route = useRoute(); // 現在のルート情報
const error = useError(); // Nuxtのエラー情報を取得

// カスタムエラーの型定義
interface CustomError {
    statusCode: number;
    statusMessage?: string;
    message?: string;
}

// エラーがカスタムエラーかどうかを判定
function isCustomError(error: any): error is CustomError {
    return error && 'statusCode' in error;
}

// サーバー側のデフォルトエラーメッセージ
const serverDefaultMessages = [
  'Server Error',
  'Forbidden',
  'Not Found',
  'Bad Request',
  'Internal Server Error',
  'Page not found'
];

// サーバー側のデフォルトエラーかどうかを判定
function isServerDefaultError(error: any): error is CustomError {
    return error
        && 'statusCode' in error
        && error.statusMessage
        && serverDefaultMessages.some(msg => error.statusMessage.includes(msg));
}

// エラーメッセージの計算
const errorMessages = computed(() => {
    if (isCustomError(error.value)) {
        const statusCode = error.value.statusCode;
        // ステータスごとのデフォルト文言
        const defaults = {
            400: {
                metaTitle: '400 Bad Request',
                title: 'エラーが発生しました',
                message: '時間をおいて再度お試しください。',
            },
            403: {
                metaTitle: '403 Forbidden',
                title: 'エラーが発生しました',
                message: '時間をおいて再度お試しください。',
            },
            404: {
                metaTitle: '404 Not Found',
                title: 'お探しのページは見つかりません',
                message: '誠に申し訳ございませんが、アクセスいただいたページが見つかりませんでした。<br/>ページが一時的に閲覧できないか、削除または移動した可能性がございます。<br/>ご不明な点がございましたら、<a href="/contact" class="error-link">こちら</a>までご連絡ください。',
            },
            500: {
                metaTitle: '500 Internal Server Error',
                title: 'システムエラーが発生しました',
                message: '時間をおいて再度お試しください。',
            },
            default: {
                metaTitle: 'Error',
                title: '予期しないエラーが発生しました',
                message: '時間をおいて再度お試しください。',
            }
        };
        // デフォルト文言を取得
        const def = defaults[statusCode as keyof typeof defaults] || defaults.default;

        // title, messageはerror.valueの値を優先し、なければデフォルト
        return {
            metaTitle: def.metaTitle,
            title: isServerDefaultError(error.value) ? def.title : (error.value as CustomError).statusMessage || def.title,
            message: isServerDefaultError(error.value) ? def.message : (error.value as CustomError).message || def.message,
            statusCode
        };
    } else {
        return {
            metaTitle: 'Error',
            title: '予期しないエラーが発生しました',
            message: '時間をおいて再度お試しください。'
        };
    }
});

// SEO設定
useHead({
    title: errorMessages.value.metaTitle, // ブラウザタブのタイトル
    meta: [
        { name: 'robots', content: 'noindex, nofollow' },
    ]
});
</script>

<template>
    <div class="error-layout">
        <Header />
        <main class="error">
            <div class="error-body">
                <HeadingCorp :level="1">{{ errorMessages.title }}</HeadingCorp>
                <p class="error-message" v-html="errorMessages.message"></p>
                <ButtonPrimary
                    v-if="['/fire/compare', '/fire/compare/'].some(prefix => route.path.startsWith(prefix)) && errorMessages.statusCode !== undefined && [400, 403, 404].includes(errorMessages.statusCode)"
                    size="md"
                    subText="保険スクエアbang!火災保険サイトへ遷移します"
                    buttonType="compare"
                >
                    条件設定画面はこちら
                </ButtonPrimary>

                <ButtonPrimary v-else size="md" buttonType="top-page">
                    トップページへ戻る
                </ButtonPrimary>
            </div>
        </main>
        <Footer />
    </div>
</template>

<style scoped lang="scss">
.error-layout {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

.error {
    position: relative;
    flex: 1;
    width: min(95vw, 64.5rem);
    margin: auto;

    &::before {
        content: '';
        position: absolute;
        width: 20rem; // 320px
        height: 20rem; // 320px
        aspect-ratio: 1/1;
        left: -4rem; // -64px
        top: -3rem; // -48px
        border-radius: 20rem; // 320px
        box-sizing: border-box;
        border: 1.5rem solid #EFF7FF; // 24px
        z-index: -1; // コンテンツの背景に配置
        pointer-events: none; // クリックイベントを通す
    }

    &-body {
        padding: 2rem 1rem 3rem 1rem;
    }

    &-message {
        width: 100%;
        color: #252522;
        font-size: 1rem;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        padding-bottom: 2.5rem;
    }

    :deep(.error-link) {
        text-decoration: underline;
        color: #2174C0;
    }
}

.error-footer {
    border-radius: 1.5rem 1.5rem 0 0; // 24px 24px 0 0
    background: linear-gradient(90deg, #EFF7FF 0%, #F0FFFF 100%);
    padding: 0.5rem 0;

    &_text {
        text-align: center;
        font-size: 0.75rem;
        color: #727268;
    }
}

.button-primary {
    margin: 0 auto;
}

@include mixin.media(pc) {

    .error {
        &::before {
            width: 32.5rem; // 520px
            height: 32.5rem; // 520px
            left: -10.7rem; // -171.2px
            top: -4rem; // -64px
            border-radius: 32.5rem; // 520px
            border: 2.5rem solid #EFF7FF; // 24px
        }

        &-body {
            padding: 3.5rem 0 6rem 0;
        }

        &-message {
            padding-bottom: 3rem;
        }
    }

    .error-footer {
        padding: 0.75rem; // 12px
    }

    .button-primary {
        margin: unset;
    }
}
</style>